import {
	AlertCircle,
	ArrowLeft,
	CheckCircle,
	ChevronRight,
	Copy,
	Folder,
	FolderPlus,
	Home,
	Loader2,
	RotateCcw,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	createFolder,
	deleteMedia,
	fetchImagesModule,
	undoMedia,
	uploadImage,
} from "../../modules/mediaManager";
import { getImageUrl } from "../../utils";

// Types
interface MediaItem {
	id: number;
	name: string;
	path: string;
	originalFilePath?: string;
	mimetype: string;
	parentId?: number;
}

interface MediaBucketResponse {
	data: MediaItem[];
	totalResult: number;
	totalPage: number;
	currentPage: number;
}

type MediaSize = "xs" | "sm" | "md" | "lg";

interface FilterState {
	search: string;
	[key: string]: string;
}

interface BreadcrumbItem {
	id: number;
	name: string;
}

// Size options
const sizeOptions: {
	size: MediaSize | "Default" | "full";
	resolution: string;
}[] = [
	{ size: "Default", resolution: "Original Image" },
	{ size: "full", resolution: "Full Size" },
	{ size: "xs", resolution: "XS (96x96px)" },
	{ size: "sm", resolution: "Small (480x320px)" },
	{ size: "md", resolution: "Medium (768x512px)" },
	{ size: "lg", resolution: "Large (1280x1024px)" },
];

// Toast Component
const Toast: React.FC<{
	message: string;
	type: "success" | "error" | "info";
	onClose: () => void;
}> = ({ message, type, onClose }) => {
	useEffect(() => {
		const timer = setTimeout(onClose, 5000);
		return () => clearTimeout(timer);
	}, [onClose]);

	const bgColor = {
		success: "bg-green-500",
		error: "bg-red-500",
		info: "bg-blue-500",
	}[type];

	const Icon = {
		success: CheckCircle,
		error: AlertCircle,
		info: AlertCircle,
	}[type];

	return (
		<div
			className={`fixed top-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 max-w-sm`}>
			<Icon size={20} />
			<span className="flex-1">{message}</span>
			<button onClick={onClose} className="hover:bg-white/20 rounded p-1">
				<X size={16} />
			</button>
		</div>
	);
};

// Breadcrumb Component
const Breadcrumb: React.FC<{
	breadcrumbs: BreadcrumbItem[];
	onNavigate: (index: number) => void;
}> = ({ breadcrumbs, onNavigate }) => {
	return (
		<div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
			<button
				onClick={() => onNavigate(-1)}
				className="flex items-center gap-1 hover:text-blue-600 transition-colors">
				<Home size={16} />
				<span>Home</span>
			</button>

			{breadcrumbs.map((item, index) => (
				<React.Fragment key={item.id}>
					<ChevronRight size={14} className="text-gray-400" />
					<button
						onClick={() => onNavigate(index)}
						className={`hover:text-blue-600 transition-colors ${
							index === breadcrumbs.length - 1
								? "font-medium text-gray-900"
								: ""
						}`}>
						{item.name}
					</button>
				</React.Fragment>
			))}
		</div>
	);
};

// DropZone Component - Fixed drag and drop
const MediaDropZone: React.FC<{
	onDrop: (files: File[]) => void;
	isUploading: boolean;
	children: React.ReactNode;
}> = ({ onDrop, isUploading, children }) => {
	const [isDragActive, setIsDragActive] = useState(false);
	const [dragCounter, setDragCounter] = useState(0);

	const handleDragEnter = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragCounter((prev) => prev + 1);
		setIsDragActive(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragCounter((prev) => {
			const newCount = prev - 1;
			if (newCount === 0) {
				setIsDragActive(false);
			}
			return newCount;
		});
	}, []);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragActive(false);
			setDragCounter(0);

			if (isUploading) return;

			const files = Array.from(e.dataTransfer.files).filter((file) =>
				file.type.startsWith("image/")
			);

			if (files.length > 0) {
				onDrop(files);
			}
		},
		[onDrop, isUploading]
	);

	return (
		<div
			className={`relative min-h-96 transition-all duration-200 rounded-lg ${
				isDragActive
					? "border-2 border-dashed border-blue-400 bg-blue-50"
					: ""
			}`}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={handleDragOver}
			onDrop={handleDrop}>
			{isDragActive && (
				<div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 z-10 rounded-lg">
					<div className="text-center">
						<Upload
							className="mx-auto text-blue-500 mb-4"
							size={48}
						/>
						<p className="text-lg font-medium text-blue-700">
							Drop images here to upload
						</p>
					</div>
				</div>
			)}
			{children}
		</div>
	);
};

// Create Folder Modal
const CreateFolderModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (name: string) => void;
	loading: boolean;
}> = ({ isOpen, onClose, onSubmit, loading }) => {
	const [folderName, setFolderName] = useState("");

	const handleSubmit = () => {
		if (folderName.trim()) {
			onSubmit(folderName.trim());
			setFolderName("");
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSubmit();
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
				<h2 className="text-xl font-bold mb-4">Create New Folder</h2>
				<div>
					<input
						type="text"
						placeholder="Folder name"
						value={folderName}
						onChange={(e) => setFolderName(e.target.value)}
						onKeyPress={handleKeyPress}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
						autoFocus
						disabled={loading}
					/>
					<div className="flex gap-3 mt-4">
						<button
							onClick={onClose}
							className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
							disabled={loading}>
							Cancel
						</button>
						<button
							onClick={handleSubmit}
							disabled={!folderName.trim() || loading}
							className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
							{loading ? (
								<Loader2 size={16} className="animate-spin" />
							) : null}
							Create
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

// Media Item Component
const MediaItemCard: React.FC<{
	item: MediaItem;
	isSelected: boolean;
	isMarkedForDeletion: boolean;
	onSelect: (id: number) => void;
	onCopy: (
		event: React.MouseEvent<HTMLButtonElement>,
		item: MediaItem
	) => void;
	onUndo: (id: number) => void;
	onFolderClick: (id: number, name: string) => void;
}> = ({
	item,
	isSelected,
	isMarkedForDeletion,
	onSelect,
	onCopy,
	onUndo,
	onFolderClick,
}) => {
	const isFolder = item.mimetype === "folder";

	return (
		<div
			className={`relative bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 ${
				isMarkedForDeletion ? "opacity-30" : "hover:shadow-lg"
			}`}>
			{/* Selection Checkbox */}
			<input
				type="checkbox"
				checked={isSelected}
				onChange={() => onSelect(item.id)}
				className="absolute top-2 right-2 w-4 h-4 text-blue-600 z-10"
			/>

			{/* Copy Button (only for images) */}
			{!isFolder && (
				<button
					onClick={(e) => onCopy(e, item)}
					className="absolute top-2 left-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 z-10">
					<Copy size={16} />
				</button>
			)}

			{/* Undo Button */}
			{isMarkedForDeletion && (
				<div className="absolute inset-0 flex items-center justify-center z-20">
					<button
						onClick={() => onUndo(item.id)}
						className="p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 border-2 border-red-500">
						<RotateCcw size={24} className="text-red-500" />
					</button>
				</div>
			)}

			{/* Content */}
			<div
				className={`h-48 cursor-pointer relative ${
					isFolder ? "cursor-pointer" : ""
				}`}
				onClick={
					isFolder
						? () => onFolderClick(item.id, item.name)
						: undefined
				}>
				{isFolder ? (
					<div className="h-full flex items-center justify-center bg-gray-100">
						<Folder size={64} className="text-gray-600" />
					</div>
				) : (
					<img
						src={getImageUrl(item.path)}
						alt={item.name}
						className="w-full h-full object-contain bg-gray-100"
						onError={(e) => {
							const target = e.target as HTMLImageElement;
							target.src =
								"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=";
						}}
					/>
				)}

				{/* Name Overlay */}
				<div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2">
					<p className="text-sm truncate">{item.name}</p>
				</div>
			</div>
		</div>
	);
};

// Copy URL Popover
const CopyUrlPopover: React.FC<{
	isOpen: boolean;
	position: { x: number; y: number };
	selectedImage: MediaItem | null;
	onClose: () => void;
	onCopy: (size: string) => void;
}> = ({ isOpen, position, selectedImage, onClose, onCopy }) => {
	if (!isOpen || !selectedImage) return null;

	return (
		<>
			<div className="fixed inset-0 z-40" onClick={onClose} />
			<div
				className="fixed bg-white rounded-lg shadow-lg border z-50 py-2 min-w-48"
				style={{ left: position.x, top: position.y }}>
				{sizeOptions.map((option) => (
					<button
						key={option.size}
						onClick={() => onCopy(option.size)}
						className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm">
						{option.resolution}
					</button>
				))}
			</div>
		</>
	);
};

// Main MediaManager Component
const MediaManager: React.FC = () => {
	// State
	const [mediaData, setMediaData] = useState<MediaItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [toast, setToast] = useState<{
		message: string;
		type: "success" | "error" | "info";
	} | null>(null);
	const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [markedForDeletion, setMarkedForDeletion] = useState<number[]>([]);
	const [filter, setFilter] = useState<FilterState>({ search: "" });

	// Pagination
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPages: 1,
		hasMore: true,
		loading: false,
	});

	// UI State
	const [showCreateFolder, setShowCreateFolder] = useState(false);
	const [copyPopover, setCopyPopover] = useState<{
		isOpen: boolean;
		position: { x: number; y: number };
		selectedImage: MediaItem | null;
	}>({ isOpen: false, position: { x: 0, y: 0 }, selectedImage: null });

	const currentParentId = useMemo(
		() =>
			breadcrumbs.length > 0
				? breadcrumbs[breadcrumbs.length - 1].id
				: null,
		[breadcrumbs]
	);

	const showToast = useCallback(
		(message: string, type: "success" | "error" | "info") => {
			setToast({ message, type });
		},
		[]
	);

	// URL Management
	useEffect(() => {
		// Read breadcrumbs from URL on mount
		const urlParams = new URLSearchParams(window.location.search);
		const pathParam = urlParams.get("path");

		if (pathParam) {
			try {
				const parsedBreadcrumbs = JSON.parse(
					decodeURIComponent(pathParam)
				) as BreadcrumbItem[];
				setBreadcrumbs(parsedBreadcrumbs);
			} catch (err) {
				console.warn("Failed to parse path from URL:", err);
			}
		}
	}, []);

	// Update URL when breadcrumbs change
	useEffect(() => {
		const url = new URL(window.location.href);

		if (breadcrumbs.length > 0) {
			url.searchParams.set(
				"path",
				encodeURIComponent(JSON.stringify(breadcrumbs))
			);
		} else {
			url.searchParams.delete("path");
		}

		// Update URL without page reload
		window.history.replaceState({}, "", url.toString());
	}, [breadcrumbs]);

	// Load media data
	const loadMediaData = useCallback(
		async (page = 1, append = false) => {
			if (pagination.loading) return;

			setPagination((prev) => ({ ...prev, loading: true }));

			try {
				const response = await fetchImagesModule(
					{
						currentPage: page,
					} as any,
					currentParentId || undefined
				);

				setMediaData((prev) =>
					append ? [...prev, ...response.data] : response.data
				);
				setPagination({
					currentPage: response.currentPage,
					totalPages: response.totalPage,
					hasMore: response.currentPage < response.totalPage,
					loading: false,
				});
			} catch (err) {
				showToast("Failed to load media. Please try again.", "error");
				setPagination((prev) => ({ ...prev, loading: false }));
			}
		},
		[currentParentId, filter.search, pagination.loading, showToast]
	);

	// Initial load and when dependencies change
	useEffect(() => {
		setMediaData([]); // Clear existing data
		loadMediaData(1, false);
	}, [currentParentId, filter.search]);

	// Infinite scroll
	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
			const threshold = 100;

			if (
				scrollHeight - scrollTop <= clientHeight + threshold &&
				pagination.hasMore &&
				!pagination.loading
			) {
				loadMediaData(pagination.currentPage + 1, true);
			}
		},
		[pagination, loadMediaData]
	);

	// File upload
	const handleFileUpload = useCallback(
		async (files: File[]) => {
			if (files.length === 0) return;

			setUploading(true);
			let successCount = 0;
			let errorCount = 0;

			for (const file of files) {
				const formData = new FormData();
				formData.append("file", file);
				formData.append("name", file.name);
				if (currentParentId) {
					formData.append("parentId", currentParentId.toString());
				}

				try {
					const result = await uploadImage(
						file,
						currentParentId || undefined
					);
					setMediaData((prev) => [result as any, ...prev]);
					successCount++;
				} catch (err) {
					errorCount++;
				}
			}

			setUploading(false);

			if (successCount > 0) {
				showToast(
					`Successfully uploaded ${successCount} file(s)`,
					"success"
				);
			}
			if (errorCount > 0) {
				showToast(`Failed to upload ${errorCount} file(s)`, "error");
			}
		},
		[currentParentId, showToast]
	);

	// Handle file input change
	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (files) {
				handleFileUpload(Array.from(files));
			}
			// Reset input value to allow uploading the same file again
			e.target.value = "";
		},
		[handleFileUpload]
	);

	// Create folder
	const handleCreateFolder = useCallback(
		async (name: string) => {
			setLoading(true);
			try {
				const folder = await createFolder(
					name,
					currentParentId || undefined
				);
				setMediaData((prev) => [folder, ...prev]);
				setShowCreateFolder(false);
				showToast("Folder created successfully", "success");
			} catch (err) {
				showToast("Failed to create folder", "error");
			} finally {
				setLoading(false);
			}
		},
		[currentParentId, showToast]
	);

	// Navigation with breadcrumbs
	const navigateToFolder = useCallback(
		(folderId: number, folderName: string) => {
			setBreadcrumbs((prev) => [
				...prev,
				{ id: folderId, name: folderName },
			]);
			setSelectedIds([]);
			setPagination((prev) => ({
				...prev,
				currentPage: 1,
				hasMore: true,
			}));
		},
		[]
	);

	const navigateToBreadcrumb = useCallback((index: number) => {
		if (index === -1) {
			// Navigate to root
			setBreadcrumbs([]);
		} else {
			// Navigate to specific breadcrumb level
			setBreadcrumbs((prev) => prev.slice(0, index + 1));
		}
		setSelectedIds([]);
		setPagination((prev) => ({ ...prev, currentPage: 1, hasMore: true }));
	}, []);

	const navigateBack = useCallback(() => {
		setBreadcrumbs((prev) => prev.slice(0, -1));
		setSelectedIds([]);
		setPagination((prev) => ({ ...prev, currentPage: 1, hasMore: true }));
	}, []);

	// Selection
	const handleItemSelect = useCallback((id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id)
				? prev.filter((itemId) => itemId !== id)
				: [...prev, id]
		);
	}, []);

	// Delete
	const handleDelete = useCallback(async () => {
		if (selectedIds.length === 0) return;

		setLoading(true);
		try {
			await deleteMedia(selectedIds);
			setMarkedForDeletion((prev) => [...prev, ...selectedIds]);
			setSelectedIds([]);
			showToast(
				`Marked ${selectedIds.length} item(s) for deletion`,
				"info"
			);

			// Auto-remove after 2 minutes
			setTimeout(() => {
				setMediaData((prev) =>
					prev.filter((item) => !selectedIds.includes(item.id))
				);
				setMarkedForDeletion((prev) =>
					prev.filter((id) => !selectedIds.includes(id))
				);
			}, 120000);
		} catch (err) {
			showToast("Failed to delete items", "error");
		} finally {
			setLoading(false);
		}
	}, [selectedIds, showToast]);

	// Undo delete
	const handleUndo = useCallback(
		async (id: number) => {
			try {
				await undoMedia(id);
				setMarkedForDeletion((prev) =>
					prev.filter((itemId) => itemId !== id)
				);
				showToast("Deletion undone successfully", "success");
			} catch (err) {
				showToast("Failed to undo deletion", "error");
			}
		},
		[showToast]
	);

	// Copy URL
	const handleCopyClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>, item: MediaItem) => {
			const rect = e.currentTarget.getBoundingClientRect();
			setCopyPopover({
				isOpen: true,
				position: { x: rect.left, y: rect.bottom + 5 },
				selectedImage: item,
			});
		},
		[]
	);

	const handleCopyUrl = useCallback(
		(size: string) => {
			if (!copyPopover.selectedImage) return;

			let url = getImageUrl(copyPopover.selectedImage.path);

			if (size === "Default") {
				url = getImageUrl(
					copyPopover.selectedImage.originalFilePath ||
						copyPopover.selectedImage.path
				);
			} else if (size !== "full") {
				url = getImageUrl(copyPopover.selectedImage.path);
			}

			navigator.clipboard.writeText(url);
			setCopyPopover((prev) => ({ ...prev, isOpen: false }));
			showToast(`Image URL copied with size: ${size}`, "success");
		},
		[copyPopover.selectedImage, showToast]
	);

	const currentFolderName = useMemo(() => {
		if (breadcrumbs.length === 0) return "All Media";
		return breadcrumbs[breadcrumbs.length - 1].name;
	}, [breadcrumbs]);

	return (
		<div className="p-6 max-w-7xl mx-auto">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-3xl font-bold text-gray-900">
					Media Manager
				</h1>
				<div className="flex gap-3">
					<button
						onClick={() => setShowCreateFolder(true)}
						className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
						<FolderPlus size={20} />
						Create Folder
					</button>
					<label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer">
						<Upload size={20} />
						Upload Photos
						<input
							type="file"
							multiple
							accept="image/*"
							onChange={handleFileInputChange}
							className="hidden"
						/>
					</label>
				</div>
			</div>

			{/* Breadcrumb Navigation */}
			<Breadcrumb
				breadcrumbs={breadcrumbs}
				onNavigate={navigateToBreadcrumb}
			/>

			{/* Navigation */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-4">
					{breadcrumbs.length > 0 && (
						<button
							onClick={navigateBack}
							className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
							<ArrowLeft size={16} />
							Back
						</button>
					)}
					<h2 className="text-xl font-semibold text-gray-700">
						{currentFolderName}
					</h2>
				</div>
				{selectedIds.length > 0 && (
					<button
						onClick={handleDelete}
						disabled={loading}
						className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
						<Trash2 size={16} />
						Delete ({selectedIds.length})
					</button>
				)}
			</div>

			{/* Media Grid */}
			<MediaDropZone onDrop={handleFileUpload} isUploading={uploading}>
				<div
					className="max-h-[70vh] overflow-auto bg-gray-50 rounded-lg p-4"
					onScroll={handleScroll}>
					{loading && mediaData.length === 0 ? (
						<div className="flex justify-center items-center h-64">
							<Loader2 className="animate-spin" size={32} />
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
							{mediaData.map((item) => (
								<MediaItemCard
									key={item.id}
									item={item}
									isSelected={selectedIds.includes(item.id)}
									isMarkedForDeletion={markedForDeletion.includes(
										item.id
									)}
									onSelect={handleItemSelect}
									onCopy={handleCopyClick}
									onUndo={handleUndo}
									onFolderClick={navigateToFolder}
								/>
							))}
						</div>
					)}

					{/* Loading indicator for pagination */}
					{pagination.loading && mediaData.length > 0 && (
						<div className="flex justify-center py-4">
							<Loader2 className="animate-spin" size={24} />
						</div>
					)}

					{/* No data message */}
					{!loading && mediaData.length === 0 && (
						<div className="text-center py-12">
							<Upload
								className="mx-auto text-gray-400 mb-4"
								size={64}
							/>
							<h3 className="text-xl font-medium text-gray-600 mb-2">
								No media found
							</h3>
							<p className="text-gray-500">
								Upload some images or create folders to get
								started
							</p>
						</div>
					)}
				</div>
			</MediaDropZone>

			{/* Upload Progress */}
			{uploading && (
				<div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-40">
					<Loader2 className="animate-spin" size={20} />
					<span>Uploading files...</span>
				</div>
			)}

			{/* Toast */}
			{toast && (
				<Toast
					message={toast.message}
					type={toast.type}
					onClose={() => setToast(null)}
				/>
			)}

			{/* Modals and Popovers */}
			<CreateFolderModal
				isOpen={showCreateFolder}
				onClose={() => setShowCreateFolder(false)}
				onSubmit={handleCreateFolder}
				loading={loading}
			/>

			<CopyUrlPopover
				isOpen={copyPopover.isOpen}
				position={copyPopover.position}
				selectedImage={copyPopover.selectedImage}
				onClose={() =>
					setCopyPopover((prev) => ({ ...prev, isOpen: false }))
				}
				onCopy={handleCopyUrl}
			/>
		</div>
	);
};

export default MediaManager;
